// Assignments' reads and writes (services: EconicAssignmentEndpoints, under the ECONIC session). Every write is the signed-in person's own
// step; the server checks who may take it. A change anywhere on a tree is pushed live as `assignment.updated` (and a bell row as
// `notification.created`), which refreshes these queries.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useAuth, useInvalidateOn } from "@ncp/api-client";
import type {
  AssignmentDetail, AssignmentListItem, AssignmentPackage, AssignmentTypeView, AssignmentView, BatchDecideResult, BriefEditInput, CommentInput, CoverInput, CoverView,
  CreateAssignmentInput, DecideInput, DelegateChildInput, MorningSummary, PersonRef, PortfolioView, ReadinessItem, ReportView, RoutingSuggestion, TypeAdminView,
  TypeValidation,
} from "./model";

export const API = "/api/econic/assignments";
/** Acting cover, office lines and the type editor: open to all staff (the server checks each right), since administration has no content clearance. */
export const ADMIN_API = "/api/econic/assignment-admin";

export const assignmentKeys = {
  all: ["econic", "assignments"] as const,
  list: (user: string | null, view: AssignmentView) => ["econic", "assignments", user, "list", view] as const,
  count: (user: string | null) => ["econic", "assignments", user, "count"] as const,
  detail: (user: string | null, id: string) => ["econic", "assignments", user, "detail", id] as const,
  reports: (user: string | null) => ["econic", "assignments", user, "reports"] as const,
  recipients: (user: string | null, onBehalfOf?: string | null) => ["econic", "assignments", user, "recipients", onBehalfOf ?? null] as const,
  routing: (text: string, kind: string | null) => ["econic", "assignments", "routing", text, kind] as const,
  types: (user: string | null, onBehalfOf?: string | null) => ["econic", "assignments", user, "types", onBehalfOf ?? null] as const,
  principals: (user: string | null) => ["econic", "assignments", user, "principals"] as const,
  portfolio: (user: string | null) => ["econic", "assignments", user, "portfolio"] as const,
  morning: (user: string | null) => ["econic", "assignments", user, "morning"] as const,
  covers: (user: string | null, all: boolean) => ["econic", "assignments", user, "covers", all] as const,
  typesManage: (user: string | null) => ["econic", "assignments", user, "types-manage"] as const,
  typeManage: (user: string | null, key: string) => ["econic", "assignments", user, "types-manage", key] as const,
};

/** Live refresh: any step on a tree the person is on, and any new bell row. */
export function useAssignmentsLive() {
  useInvalidateOn(["assignment.updated", "notification.created"], [[...assignmentKeys.all]]);
}

export function useAssignments(view: AssignmentView, take = 50, enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.list(user?.id ?? null, view),
    queryFn: () => api.get<{ items: AssignmentListItem[] }>(API, { view, take }).then((r) => r.items),
    enabled: enabled && !!user,
    staleTime: 10_000,
  });
}

/** How many assignments wait on the person (My workspace's entry, the bell). */
export function useAssignmentCount(enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.count(user?.id ?? null),
    queryFn: () => api.get<{ action: number }>(`${API}/count`).then((r) => r.action),
    enabled: enabled && !!user,
    staleTime: 30_000,
  });
}

export function useAssignment(id: string | null | undefined) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.detail(user?.id ?? null, id ?? ""),
    queryFn: () => api.get<AssignmentDetail>(`${API}/${encodeURIComponent(id!)}`),
    enabled: !!user && !!id,
    staleTime: 5_000,
  });
}

/** The viewer's direct reports (whom they may delegate to), with each one's open load. */
export function useDirectReports(enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.reports(user?.id ?? null),
    queryFn: () => api.get<{ items: ReportView[] }>(`${API}/people/reports`).then((r) => r.items),
    enabled: enabled && !!user,
    staleTime: 60_000,
  });
}

/** Whom a new request may go to: the requester's direct reports and every department's director (an office: its principal's). */
export function useRecipients(enabled = true, onBehalfOf?: string | null) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.recipients(user?.id ?? null, onBehalfOf),
    queryFn: () => api.get<{ items: ReportView[] }>(`${API}/people/recipients`, onBehalfOf ? { onBehalfOf } : undefined).then((r) => r.items),
    enabled: enabled && !!user,
    staleTime: 60_000,
  });
}

/** The types of request in use, each saying whether the viewer (or their principal) may pick it, with its defaults and journey. */
export function useAssignmentTypes(enabled = true, onBehalfOf?: string | null) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.types(user?.id ?? null, onBehalfOf),
    queryFn: () => api.get<{ items: AssignmentTypeView[] }>(`${API}/types`, onBehalfOf ? { onBehalfOf } : undefined).then((r) => r.items),
    enabled: enabled && !!user,
    staleTime: 5 * 60_000,
  });
}

/** The principals whose office the viewer is (requests may be raised on their behalf). */
export function usePrincipals(enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.principals(user?.id ?? null),
    queryFn: () => api.get<{ items: PersonRef[] }>(`${API}/principals`).then((r) => r.items),
    enabled: enabled && !!user,
    staleTime: 5 * 60_000,
  });
}

/** The leader's portfolio: every request they asked for (or their principals did), with its health. */
export function usePortfolio(enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.portfolio(user?.id ?? null),
    queryFn: () => api.get<PortfolioView>(`${API}/portfolio`),
    enabled: enabled && !!user,
    staleTime: 15_000,
  });
}

/** "Brief me on my assignments": what waits on the person, what is late or at risk, the updates since yesterday. */
export function useMorning(enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.morning(user?.id ?? null),
    queryFn: () => api.get<MorningSummary>(`${API}/morning`),
    enabled: enabled && !!user,
    staleTime: 60_000,
  });
}

/** Acting cover and office lines the viewer is part of (administration: all of them). */
export function useCovers(all = false, enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.covers(user?.id ?? null, all),
    queryFn: () => api.get<{ items: CoverView[] }>(`${ADMIN_API}/covers`, all ? { all: true } : undefined).then((r) => r.items),
    enabled: enabled && !!user,
    staleTime: 30_000,
  });
}

/** The type editor's list (Digital Transformation administration, Knowledge Management). */
export function useTypesManage(enabled = true) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.typesManage(user?.id ?? null),
    queryFn: () => api.get<{ items: TypeAdminView[] }>(`${ADMIN_API}/types`).then((r) => r.items),
    enabled: enabled && !!user,
    staleTime: 15_000,
  });
}

export function useTypeManage(key: string | null | undefined) {
  const api = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: assignmentKeys.typeManage(user?.id ?? null, key ?? ""),
    queryFn: () => api.get<TypeAdminView>(`${ADMIN_API}/types/${encodeURIComponent(key!)}`),
    enabled: !!user && !!key,
    staleTime: 10_000,
  });
}

/** The department (and its director) a request is suggested to go to. */
export function useRouting(text: string, kind: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: assignmentKeys.routing(text, kind),
    queryFn: () => api.get<RoutingSuggestion>(`${API}/routing`, { text, kind: kind ?? undefined }),
    enabled: enabled && text.trim().length > 0,
    staleTime: 60_000,
  });
}

/** Every step a person may take on a node; each refreshes the node, the lists and the count. */
export function useAssignmentActions() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: assignmentKeys.all });
  const at = (id: string, step: string) => `${API}/${encodeURIComponent(id)}/${step}`;
  return {
    create: useMutation({ mutationFn: (v: CreateAssignmentInput) => api.post<AssignmentDetail>(API, v), onSettled: refresh }),
    accept: useMutation({ mutationFn: (id: string) => api.post<AssignmentDetail>(at(id, "accept"), {}), onSettled: refresh }),
    /** Accept, proposing another due date for the person who assigned it to agree to. */
    acceptWithDate: useMutation({
      mutationFn: (v: { id: string; proposedDueAt: string; reason: string }) => api.post<AssignmentDetail>(at(v.id, "accept"), { proposedDueAt: v.proposedDueAt, reason: v.reason }),
      onSettled: refresh,
    }),
    decline: useMutation({ mutationFn: (v: { id: string; reason: string }) => api.post<AssignmentDetail>(at(v.id, "decline"), { reason: v.reason }), onSettled: refresh }),
    reassign: useMutation({
      mutationFn: (v: { id: string; ownerUserId: string; note?: string }) => api.post<AssignmentDetail>(at(v.id, "reassign"), { ownerUserId: v.ownerUserId, note: v.note }),
      onSettled: refresh,
    }),
    delegate: useMutation({
      mutationFn: (v: { id: string; children: DelegateChildInput[] }) => api.post<AssignmentDetail>(at(v.id, "delegate"), { children: v.children }),
      onSettled: refresh,
    }),
    savePackage: useMutation({
      mutationFn: (v: { id: string; package: AssignmentPackage }) => api.put<AssignmentPackage>(at(v.id, "package"), { package: v.package }),
      onSettled: refresh,
    }),
    submit: useMutation({
      mutationFn: (v: { id: string; package?: AssignmentPackage; checklist?: DecideInput["checklist"] }) =>
        api.post<AssignmentDetail>(at(v.id, "submit"), { package: v.package, checklist: v.checklist }),
      onSettled: refresh,
    }),
    decide: useMutation({ mutationFn: (v: { id: string } & DecideInput) => { const { id, ...body } = v; return api.post<AssignmentDetail>(at(id, "decide"), body); }, onSettled: refresh }),
    cancel: useMutation({ mutationFn: (v: { id: string; reason?: string }) => api.post<AssignmentDetail>(at(v.id, "cancel"), { reason: v.reason }), onSettled: refresh }),
    /** A comment (anchored or not), a question (pauses the clock), an answer, or a progress note. */
    comment: useMutation({ mutationFn: (v: { id: string } & CommentInput) => { const { id, ...body } = v; return api.post<AssignmentDetail>(at(id, "comments"), body); }, onSettled: refresh }),
    refile: useMutation({ mutationFn: (id: string) => api.post<AssignmentDetail>(at(id, "refile"), {}), onSettled: refresh }),
    extension: useMutation({
      mutationFn: (v: { id: string; dueAt: string; reason: string }) => api.post<AssignmentDetail>(at(v.id, "extension"), { dueAt: v.dueAt, reason: v.reason }),
      onSettled: refresh,
    }),
    decideExtension: useMutation({
      mutationFn: (v: { id: string; decision: "approve" | "decline"; note?: string }) => api.post<AssignmentDetail>(at(v.id, "extension/decide"), { decision: v.decision, note: v.note }),
      onSettled: refresh,
    }),
    hold: useMutation({ mutationFn: (v: { id: string; reason?: string }) => api.post<AssignmentDetail>(at(v.id, "hold"), { reason: v.reason }), onSettled: refresh }),
    resume: useMutation({ mutationFn: (id: string) => api.post<AssignmentDetail>(at(id, "resume"), {}), onSettled: refresh }),
    editBrief: useMutation({ mutationFn: (v: { id: string } & BriefEditInput) => { const { id, ...body } = v; return api.put<AssignmentDetail>(at(id, "brief"), body); }, onSettled: refresh }),
    acknowledgeBrief: useMutation({ mutationFn: (id: string) => api.post<AssignmentDetail>(at(id, "brief/acknowledge"), {}), onSettled: refresh }),
    /** Pass the request to another department's director (the person who redirects it no longer sees it). */
    redirect: useMutation({
      mutationFn: (v: { id: string; ownerUserId: string; note: string }) => api.post<{ id: string; redirected: boolean }>(at(v.id, "redirect"), { ownerUserId: v.ownerUserId, note: v.note }),
      onSettled: refresh,
    }),
    merge: useMutation({ mutationFn: (v: { id: string; intoId: string; note?: string }) => api.post<AssignmentDetail>(at(v.id, "merge"), { intoId: v.intoId, note: v.note }), onSettled: refresh }),
    watchers: useMutation({
      mutationFn: (v: { id: string; add?: string[]; remove?: string[] }) => api.put<AssignmentDetail>(at(v.id, "watchers"), { add: v.add, remove: v.remove }),
      onSettled: refresh,
    }),
    consolidate: useMutation({
      mutationFn: (v: { id: string; replace?: boolean }) => api.post<AssignmentDetail>(`${at(v.id, "package/consolidate")}${v.replace ? "?replace=true" : ""}`, {}),
      onSettled: refresh,
    }),
    /** The readiness check of a package before it is sent (not saved). */
    readiness: useMutation({
      mutationFn: (v: { id: string; package: AssignmentPackage }) => api.post<{ items: ReadinessItem[] }>(at(v.id, "readiness"), { package: v.package }).then((r) => r.items),
    }),
    /** Several packages approved (or acknowledged) at once; each item says whether it went through. */
    decideBatch: useMutation({
      mutationFn: (v: { items: { id: string; revision?: number }[]; note?: string }) => api.post<{ items: BatchDecideResult[] }>(`${API}/decide-batch`, v).then((r) => r.items),
      onSettled: refresh,
    }),
    /** The owner's conversation bound to the assignment (opening it accepts the assignment). */
    thread: useMutation({
      mutationFn: (v: { id: string; space?: string | null; locale?: string }) =>
        api.post<{ threadId: string; created: boolean; workspaceKey: string }>(`${at(v.id, "thread")}?${new URLSearchParams({ ...(v.space ? { space: v.space } : {}), locale: v.locale ?? "en" })}`, {}),
      onSettled: refresh,
    }),
  };
}

/** Acting cover and office lines (Settings, and administration in the console). */
export function useCoverActions() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: assignmentKeys.all });
  return {
    set: useMutation({ mutationFn: (v: CoverInput) => api.post<CoverView>(`${ADMIN_API}/covers`, v), onSettled: refresh }),
    cancel: useMutation({ mutationFn: (id: string) => api.delete<void>(`${ADMIN_API}/covers/${encodeURIComponent(id)}`), onSettled: refresh }),
  };
}

/** The type editor's steps: a draft, its review by a second person, retire or restore, and validation as one types. */
export function useTypeActions() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: assignmentKeys.all });
  const at = (key: string, step: string) => `${ADMIN_API}/types/${encodeURIComponent(key)}/${step}`;
  return {
    saveDraft: useMutation({
      mutationFn: (v: { key: string; definition: Record<string, unknown>; changeNote?: string }) => api.put<TypeAdminView>(at(v.key, "draft"), { definition: v.definition, changeNote: v.changeNote }),
      onSettled: refresh,
    }),
    discardDraft: useMutation({ mutationFn: (key: string) => api.delete<void>(at(key, "draft")), onSettled: refresh }),
    submit: useMutation({ mutationFn: (key: string) => api.post<TypeAdminView>(at(key, "submit"), {}), onSettled: refresh }),
    decide: useMutation({
      mutationFn: (v: { key: string; decision: "approve" | "reject"; note?: string }) => api.post<TypeAdminView>(at(v.key, "decide"), { decision: v.decision, note: v.note }),
      onSettled: refresh,
    }),
    retire: useMutation({ mutationFn: (v: { key: string; restore?: boolean }) => api.post<TypeAdminView>(`${at(v.key, "retire")}${v.restore ? "?restore=true" : ""}`, {}), onSettled: refresh }),
    validate: useMutation({ mutationFn: (definition: Record<string, unknown>) => api.post<TypeValidation>(`${ADMIN_API}/types/validate`, { definition }) }),
  };
}

/** The server's reason for a refused step in the reader's language (problem details: `title` in English, `messageAr`), or null. */
export function assignmentError(e: unknown, lang: "en" | "ar"): string | null {
  if (!(e instanceof ApiError)) return e instanceof Error && e.message ? e.message : null;
  const p = e.problem as (Record<string, unknown> | undefined);
  const ar = typeof p?.messageAr === "string" && p.messageAr ? p.messageAr : null;
  return (lang === "ar" && ar) || e.problem?.title || e.problem?.error || e.message || null;
}

/** The URL of a pinned deliverable's file (opened in a new tab or downloaded). */
export const deliverableUrl = (assignmentId: string, submissionNumber: number, key: string, lang: "en" | "ar") =>
  `${API}/${encodeURIComponent(assignmentId)}/submissions/${submissionNumber}/deliverables/${encodeURIComponent(key)}?lang=${lang}`;
